import { metrics } from '@seek/metrics-js';
import { useEffect } from 'react';

import { useSelector } from 'src/store/react';
import { selectPrevPathname } from 'src/store/selectors';

const metricSent: Record<string, boolean> = {};

/**
 * Measure the time it takes for a component to mount since the response end.
 * This is useful for measuring the time it takes for a component to mount after the initial page load.
 *
 * The hook will only publish the metric only on the initial rendering from server side and the component is hydrated.
 * (eg: to avoid the case of measuring the time it takes for a component to mount after a client-side navigation)
 *
 * @param componentName The name of the component to measure the time for.
 * @param hasLoaded Whether the component has loaded or not. Default is true.
 * This is useful for components that have loading states.
 */
const useMeasureTimeFirstMountFromResponseEnd = (
  componentName: string,
  hasLoaded: boolean = true,
) => {
  const previousPath = useSelector(selectPrevPathname);

  useEffect(() => {
    if (hasLoaded) {
      // Only measure the time for the first time rendering (aka being rendered first time on the client side and therefore no previous path)
      if (metricSent[componentName] || previousPath) {
        return;
      }

      metricSent[componentName] = true;

      const startTime = getResponseEndStartTime();
      if (!startTime) {
        return;
      }

      const duration = performance.now() - startTime;

      // Discard any duration that is more than 60 seconds
      if (duration < 60 * 1000) {
        metrics.timing('component.timeToFirstMountFromResponseEnd', duration, [
          `component:${componentName}`,
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded]);
};

/**
 * returns the number of milliseconds for when the response end
 * time finished (after the html doc is downloaded to client) since the epoch.
 *
 * Returns undefined if the window object/navigation timing is not available or if
 * time origin data is not reliable.
 */
const getResponseEndStartTime = () => {
  if (!window) {
    return;
  }

  const performanceNavigationTiming = window.performance?.getEntriesByType(
    'navigation',
  )?.[0] as PerformanceNavigationTiming;

  if (!performanceNavigationTiming?.responseEnd) {
    return;
  }

  return performanceNavigationTiming.responseEnd;
};

export default useMeasureTimeFirstMountFromResponseEnd;
